/* eslint-disable react/no-unstable-nested-components */
import { FatSelectItem, Select, SelectContent, SelectTrigger, SelectValue } from "components/ui/Select";
import Twemoji from "components/ui/Twemoji";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import { FormattedDisplayName, FormattedMessage } from "react-intl";

export const AVAILABLE_CURRENCIES = [
  { currency: "USD", emoji: "🇺🇸" },
  { currency: "BRL", emoji: "🇧🇷" },
  { currency: "EUR", emoji: "🇪🇺" },
  { currency: "GBP", emoji: "🇬🇧" },
  { currency: "JPY", emoji: "🇯🇵" },
  { currency: "AUD", emoji: "🇦🇺" },
  { currency: "CAD", emoji: "🇨🇦" },
];

export const AVAILABLE_CURRENCY_CODES = AVAILABLE_CURRENCIES.map(({ currency }) => currency);

interface SelectFinancialsCurrencyProps {
  disabled?: boolean;
  value: string;
  hideChevronIcon?: boolean;
  onChange?: (value: string) => void;
}
export default function SelectFinancialsCurrency({
  value,
  onChange,
  disabled = false,
  hideChevronIcon = false,
}: SelectFinancialsCurrencyProps) {
  const requestedCurrencyEmoji = AVAILABLE_CURRENCIES.find(({ currency }) => currency === value)?.emoji;
  const theme = useTheme();

  return (
    <Select disabled={disabled} defaultValue={value} onValueChange={(value) => onChange?.(value)}>
      <SelectTrigger
        className={classNames(
          "flex",
          "w-full min-w-20 border border-slate-200 bg-white sm:max-w-36",
          "font-aeonik text-xs font-medium tracking-wide text-slate-600",
          "hover:ring-2 focus-visible:ring-2",
          "hover:ring-offset-1 focus-visible:ring-offset-1",
          "hover:border-transparent hover:ring-slate-100 hover:ring-offset-slate-300",
          "focus-visible:border-transparent focus-visible:ring-indigo-100 focus-visible:ring-offset-indigo-300",
          "disabled:select-none",
          "truncate",
          "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
          "dark:hover:ring-slate-900 dark:hover:ring-offset-slate-900",
          "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
        )}
        hideChevronIcon={hideChevronIcon}
      >
        <SelectValue>
          <div className="flex items-center space-x-1">
            {requestedCurrencyEmoji && <Twemoji emoji={requestedCurrencyEmoji} />}
            <span className="flex space-x-0.5">
              <span>{value}</span>
              <small className="hidden text-xs md:flex">
                <FormattedMessage defaultMessage="(milhões)" id="Q67yMe" />
              </small>
            </span>
          </div>
        </SelectValue>
      </SelectTrigger>

      <SelectContent
        position="popper"
        className={classNames("min-w-44 rounded-lg border border-slate-300 text-sm", {
          "border-slate-900/80 bg-slate-900 text-slate-300 shadow-neutral-950/40": theme === "dark",
          dark: theme === "dark",
        })}
      >
        {AVAILABLE_CURRENCIES.map(({ currency, emoji }) => {
          return (
            <FatSelectItem key={currency} className="cursor-pointer rounded-lg text-sm" value={currency}>
              <div className="flex items-center space-x-2">
                <h4 className="text-xs font-bold text-slate-600 dark:text-slate-300">{currency}</h4>
                <Twemoji emoji={emoji} />
              </div>

              <p className="text-xs text-slate-500">
                <FormattedDisplayName type="currency" value={currency} />
              </p>
            </FatSelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
